import { useState, useEffect, useCallback } from 'react';
import { FunktionarerAPI, ProtectedFunktionarerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/* ── color palette (distinct from board) ──────────────────────────────── */

export const FUNKTIONARER_COLORS = [
  { label: 'Grå',     value: '#5a6068' },
  { label: 'Brun',    value: '#8b6914' },
  { label: 'Lila',    value: '#6b3d7a' },
  { label: 'Teal',    value: '#3d6b6b' },
  { label: 'Orange',  value: '#8b4513' },
  { label: 'Mörkgrön',value: '#2d4a2e' },
];

export const DEFAULT_FUNKTIONARER_COLOR = FUNKTIONARER_COLORS[0].value;

/* ── toast ───────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.88rem'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

/* ── member drawer ───────────────────────────────────────────────────── */

const EMPTY_MEMBER = { roll: '', namn: '', telefon: '', epost: '', adress: '', beskrivning: '', color: DEFAULT_FUNKTIONARER_COLOR };

function MemberDrawer({ open, onClose, onSave, member }) {
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(member ? {
      roll:        member.roll        || '',
      namn:        member.namn        || '',
      telefon:     member.telefon     || '',
      epost:       member.epost       || '',
      adress:      member.adress      || '',
      beskrivning: member.beskrivning || '',
      color:       member.color       || DEFAULT_FUNKTIONARER_COLOR,
    } : { ...EMPTY_MEMBER });
    setError('');
  }, [open, member]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.roll.trim()) { setError('Roll är obligatorisk'); return; }
    if (!form.namn.trim()) { setError('Namn är obligatoriskt'); return; }
    setSaving(true);
    try {
      await onSave({ ...form, roll: form.roll.trim(), namn: form.namn.trim() });
      onClose();
    } catch (err) {
      setError(err.message || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const label = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    color: 'var(--text-soft)', marginBottom: '0.4rem'
  };

  const input = {
    width: '100%', padding: '0.65rem 0.9rem', border: '1px solid var(--border)',
    borderRadius: 4, fontSize: '0.95rem', fontFamily: "'Lato', sans-serif",
    background: '#fff', color: 'var(--text)'
  };

  return (
    <>
      {open && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 200,
          opacity: open ? 1 : 0, transition: 'opacity .2s'
        }} />
      )}

      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 420,
        background: 'var(--white)', zIndex: 210,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform .25s cubic-bezier(.4,0,.2,1)',
        display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,.15)'
      }}>
        <div style={{ height: 6, background: form.color, transition: 'background .2s' }} />

        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', color: 'var(--forest)', margin: 0 }}>
            {member ? 'Redigera funktionär' : 'Lägg till funktionär'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--text-soft)', padding: '0.2rem 0.4rem' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {error && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(176,48,48,.08)', border: '1px solid rgba(176,48,48,.3)', borderRadius: 4, fontSize: '0.88rem', color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          <div>
            <label style={label}>Roll / titel</label>
            <input style={input} value={form.roll} onChange={e => set('roll', e.target.value)} placeholder="T.ex. Revisor, Valberedning…" />
          </div>

          <div>
            <label style={label}>Namn</label>
            <input style={input} value={form.namn} onChange={e => set('namn', e.target.value)} placeholder="Förnamn Efternamn" />
          </div>

          <div>
            <label style={label}>Beskrivning / ansvarsområde</label>
            <input
              style={input}
              value={form.beskrivning}
              onChange={e => set('beskrivning', e.target.value)}
              placeholder="T.ex. Granskar föreningens ekonomi"
            />
          </div>

          <div>
            <label style={label}>Adress</label>
            <input
              style={input}
              value={form.adress}
              onChange={e => set('adress', e.target.value)}
              placeholder="T.ex. Hannäsvägen 164, 642 94 Flen"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={label}>Telefon</label>
              <input style={input} type="tel" value={form.telefon} onChange={e => set('telefon', e.target.value)} placeholder="070-000 00 00" />
            </div>
            <div>
              <label style={label}>E-post</label>
              <input style={input} type="email" value={form.epost} onChange={e => set('epost', e.target.value)} placeholder="namn@example.com" />
            </div>
          </div>

          <div>
            <label style={label}>Kortfärg</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {FUNKTIONARER_COLORS.map(c => (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  onClick={() => set('color', c.value)}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: c.value, border: 'none', cursor: 'pointer',
                    outline: form.color === c.value ? `3px solid ${c.value}` : '3px solid transparent',
                    outlineOffset: 2,
                    transition: 'outline .15s'
                  }}
                />
              ))}
            </div>
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-ghost" style={{ flex: 1 }}>Avbryt</button>
            <button type="submit" disabled={saving} className="btn btn-forest" style={{ flex: 2 }}>
              {saving ? 'Sparar…' : member ? 'Spara ändringar' : 'Lägg till'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── card component (smaller, muted style) ───────────────────────────── */

function FunktionarerCard({ member, index, total, onEdit, onDelete, onMove, canEdit }) {
  const [hov, setHov] = useState(false);
  const accent = member.color || '#5a6068';
  const isVacant = !member.namn || member.namn.toLowerCase().startsWith('vakant');

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        padding: '0.75rem 1rem',
        background: 'var(--white)', borderRadius: 5,
        borderLeft: `3px solid ${isVacant ? 'var(--border)' : accent}`,
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
        transition: 'box-shadow .15s'
      }}
    >
      {/* reorder buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', flexShrink: 0 }}>
        <button
          onClick={() => canEdit && onMove(index, -1)}
          disabled={!canEdit || index === 0}
          title={canEdit ? '' : 'Kräver administratörsbehörighet'}
          style={{
            background: 'none', border: 'none', cursor: canEdit && index !== 0 ? 'pointer' : 'not-allowed',
            color: 'var(--text-soft)', fontSize: '0.7rem', padding: '0.1rem 0.25rem',
            opacity: !canEdit || index === 0 ? 0.25 : 0.6, lineHeight: 1
          }}>▲</button>
        <button
          onClick={() => canEdit && onMove(index, 1)}
          disabled={!canEdit || index === total - 1}
          title={canEdit ? '' : 'Kräver administratörsbehörighet'}
          style={{
            background: 'none', border: 'none', cursor: canEdit && index !== total - 1 ? 'pointer' : 'not-allowed',
            color: 'var(--text-soft)', fontSize: '0.7rem', padding: '0.1rem 0.25rem',
            opacity: !canEdit || index === total - 1 ? 0.25 : 0.6, lineHeight: 1
          }}>▼</button>
      </div>

      {/* info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent, marginBottom: '0.15rem' }}>
          {member.roll}
        </div>
        <div style={{ fontSize: '0.85rem', fontWeight: 500, color: isVacant ? 'var(--text-soft)' : 'var(--text)', fontStyle: isVacant ? 'italic' : 'normal' }}>
          {member.namn || '—'}
        </div>
        {member.beskrivning && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)', marginTop: '0.1rem' }}>
            {member.beskrivning}
          </div>
        )}
        <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)', marginTop: '0.15rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {member.adress && <span>📍 {member.adress}</span>}
          {member.telefon && <span>📞 {member.telefon}</span>}
          {member.epost && <span>✉ {member.epost}</span>}
        </div>
      </div>

      {/* actions */}
      <div style={{ display: 'flex', gap: '0.2rem', flexShrink: 0, opacity: hov ? 1 : 0.3, transition: 'opacity .15s' }}>
        <button
          onClick={() => canEdit && onEdit(member)}
          disabled={!canEdit}
          style={{
            background: 'none', border: 'none', cursor: canEdit ? 'pointer' : 'not-allowed',
            padding: '0.3rem 0.4rem', borderRadius: 3, color: canEdit ? 'var(--text-soft)' : '#9CA3AF', fontSize: '0.8rem'
          }}
        >✏</button>
        <button
          onClick={() => canEdit && onDelete(index)}
          disabled={!canEdit}
          style={{
            background: 'none', border: 'none', cursor: canEdit ? 'pointer' : 'not-allowed',
            padding: '0.3rem 0.4rem', borderRadius: 3, color: canEdit ? 'var(--danger)' : '#9CA3AF', fontSize: '0.8rem'
          }}
        >🗑</button>
      </div>
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function FunktionarerManager() {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canEdit = hasRole('contentCreator');
  const [funktionarer, setFunktionarer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);

  const show = (m, t = 'success') => setToast({ message: m, type: t });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await FunktionarerAPI.getAll();
      setFunktionarer(res.funktionarer || []);
    } catch {
      show('Kunde inte ladda funktionärer', 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = async (list) => {
    setSaving(true);
    try {
      await ProtectedFunktionarerAPI.update(list.map((m, i) => ({ ...m, sort_order: i + 1 })));
      await load();
      show('Sparat');
    } catch {
      show('Kunde inte spara', 'error');
    } finally { setSaving(false); }
  };

  const move = async (i, dir) => {
    if (!isAdmin) return;
    const list = [...funktionarer];
    const [moved] = list.splice(i, 1);
    list.splice(i + dir, 0, moved);
    await persist(list);
  };

  const add = async (member) => {
    if (!isAdmin) return;
    const list = [...funktionarer, { ...member, id: `tmp-${Date.now()}` }];
    await persist(list);
  };

  const update = async (member) => {
    if (!isAdmin) return;
    const list = funktionarer.map(m => m.id === editing.id ? { ...member, id: m.id } : m);
    await persist(list);
    setEditing(null);
  };

  const remove = async (idx) => {
    if (!isAdmin) return;
    if (!confirm('Ta bort denna funktionär?')) return;
    const list = funktionarer.filter((_, i) => i !== idx);
    await persist(list);
  };

  const openAdd = () => { setEditing(null); setShowDrawer(true); };
  const openEdit = (m) => { setEditing(m); setShowDrawer(true); };
  const closeDrawer = () => setShowDrawer(false);

  const onSave = (member) => editing ? update(member) : add(member);

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 900, margin: '0 auto' }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', color: 'var(--forest)', margin: 0 }}>
            Funktionärer
          </h1>
          <p style={{ color: 'var(--text-soft)', margin: '0.25rem 0 0' }}>
            Övriga funktionärer som revisorer, valberedning m.m.
          </p>
        </div>

        {isAdmin && (
          <button onClick={openAdd} className="btn btn-forest" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>+</span> Lägg till
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-soft)' }}>Laddar…</div>
      ) : funktionarer.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-soft)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📋</div>
          <p>Inga funktionärer tillagda ännu</p>
          {isAdmin && <button onClick={openAdd} className="btn btn-forest" style={{ marginTop: '1rem' }}>Lägg till första funktionären</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {funktionarer.map((m, i) => (
            <FunktionarerCard
              key={m.id || i}
              member={m}
              index={i}
              total={funktionarer.length}
              onEdit={openEdit}
              onDelete={remove}
              onMove={move}
              canEdit={isAdmin}
            />
          ))}
        </div>
      )}

      {saving && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--forest)', color: '#fff', padding: '0.6rem 1.2rem', borderRadius: 4,
          fontSize: '0.85rem', boxShadow: '0 2px 8px rgba(0,0,0,.2)'
        }}>
          Sparar…
        </div>
      )}

      <MemberDrawer
        open={showDrawer}
        onClose={closeDrawer}
        onSave={onSave}
        member={editing}
      />
    </div>
  );
}
